import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/components/ui/form';
import { Pen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { hash } from '@/utils/hash';
import useEditPassword from '../hooks/useEditPassword';

const UserPasswordForm = () => {
	const { t } = useTranslation();
	const { editPassword } = useEditPassword();

	const passwordSchema = z
		.object({
			oldPassword: z.string().min(1, t('common.error.required')),
			newPassword: z.string().min(8, t('password.form.error.length')),
			confirmPassword: z.string().min(1, t('common.error.required')),
		})
		.refine((data) => data.newPassword === data.confirmPassword, {
			message: t('password.form.error.match'),
			path: ['confirmPassword'],
		})
		.refine((data) => data.oldPassword !== data.newPassword, {
			message: t('password.form.error.same'),
			path: ['newPassword'],
		});

	type PasswordFormValues = z.infer<typeof passwordSchema>;

	const form = useForm<PasswordFormValues>({
		resolver: zodResolver(passwordSchema),
		defaultValues: {
			oldPassword: '',
			newPassword: '',
			confirmPassword: '',
		},
	});

	function onSubmit(values: PasswordFormValues) {
		values.confirmPassword = hash(values.confirmPassword);
		values.oldPassword = hash(values.oldPassword);
		values.newPassword = hash(values.newPassword);
		editPassword(values);
		form.setValue('oldPassword', '');
		form.setValue('newPassword', '');
		form.setValue('confirmPassword', '');
	}

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				className="flex flex-col space-y-4"
			>
				<div>
					<FormField
						control={form.control}
						name="oldPassword"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Input
										label={t('password.form.current.title')}
										type="password"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="newPassword"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Input
										label={t('password.form.new.title')}
										type="password"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="confirmPassword"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Input
										label={t('password.form.confirm.title')}
										type="password"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<Button className="w-fit self-end" type="submit">
					<Pen className="mr-2 h-4 w-4" />
					{t('password.form.submit')}
				</Button>
			</form>
		</Form>
	);
};

export default UserPasswordForm;
